import React, { useMemo } from 'react';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { CalculatorType, CalculatorOption } from '../../types/calculator.types';
import { Label } from '@components/atoms/Label';
import { useTranslation } from '@hooks/useTranslation';

interface CalculatorSelectorProps {
  selectedCalculator: CalculatorType;
  onCalculatorChange: (calculator: CalculatorType) => void;
}

const CalculatorSelector: React.FC<CalculatorSelectorProps> = ({
  selectedCalculator,
  onCalculatorChange,
}) => {
  const tCalc = useTranslation('calculators');

  // Sprint 30, story-151 review Critical #1. This array was previously a module-level constant
  // hardcoded in English — invisible to `hardcoded-label-table` because an array-of-objects
  // never matched its object-literal-only check, and never wired to `tCalc` at all. Built inside
  // the component now so it can read the catalog's already-correct Spanish names/descriptions.
  const calculatorOptions: CalculatorOption[] = useMemo(
    () => [
      { value: CalculatorType.TILE, label: tCalc.names.tile, description: tCalc.names.tileDescription },
      {
        value: CalculatorType.CONCRETE,
        label: tCalc.names.concrete,
        description: tCalc.names.concreteDescription,
      },
      {
        value: CalculatorType.FLOORING,
        label: tCalc.names.flooring,
        description: tCalc.names.flooringDescription,
      },
      {
        value: CalculatorType.FRAMING,
        label: tCalc.names.framing,
        description: tCalc.names.framingDescription,
      },
      {
        value: CalculatorType.PAINT,
        label: tCalc.names.paint,
        description: tCalc.names.paintDescription,
      },
      {
        value: CalculatorType.RETAINING_WALL,
        label: tCalc.names.retainingWall,
        description: tCalc.names.retainingWallDescription,
      },
      {
        value: CalculatorType.ELECTRICAL,
        label: tCalc.names.electrical,
        description: tCalc.names.electricalDescription,
      },
      {
        value: CalculatorType.PLUMBING,
        label: tCalc.names.plumbing,
        description: tCalc.names.plumbingDescription,
      },
    ],
    [tCalc],
  );

  const selectOptions: SelectOption[] = useMemo(
    () =>
      calculatorOptions.map((option) => ({
        value: option.value,
        label: `${option.label} - ${option.description}`,
      })),
    [calculatorOptions],
  );

  const selectedOption = selectOptions.find(
    (opt) => opt.value === selectedCalculator,
  );

  return (
    <div className="mb-6">
      <Label label={tCalc.names.selectCalculator} htmlFor="calculator-select" />
      <div className="mt-1">
        <ReactSelect
          options={selectOptions}
          value={selectedOption}
          onChange={(option) => {
            if (option && 'value' in option) {
              onCalculatorChange(option.value as CalculatorType);
            }
          }}
          placeholder={tCalc.names.selectCalculatorPlaceholder}
          isClearable={false}
          isSearchable={false}
        />
      </div>
    </div>
  );
};

export default CalculatorSelector;
